/* eslint-disable nimbus/prefer-until-destroyed */
import { distinctUntilChanged, skip } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { ConfigurationService } from '../configuration.service';

export class BaseAuthenticatedPollingService {
  private interval: NodeJS.Timeout;
  private intervalTimeoutSec: number = 0;
  private pollFailures: number = 0;

  private get startingFailureIntervalSec(): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.configurationService.configurationSettings$.value?.basePollingStartingFailureIntervalSec ?? 10;
  }

  public get defaultTimeoutSec(): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.configurationService.configurationSettings$.value?.basePollingDefaultTimeoutSec ?? 600;
  }
  public get maxRetryTimeoutSec(): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.configurationService.configurationSettings$.value?.basePollingMaxRetryTimeoutSec ?? 600;
  }

  public constructor(
    protected readonly authService: AuthService,
    protected readonly configurationService: ConfigurationService,
  ) {
    this.initialize();
  }

  public initialize(): void {
    this.authService.authenticated$.pipe(skip(1), distinctUntilChanged()).subscribe((x) => {
      if (x) {
        this.restartPolling();
      } else {
        this.stopPolling();
      }
    });
  }

  public restartPolling(): void {
    this.resetPollingInterval();
    this.pollingAction();
  }

  public stopPolling(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.stopPollingAction();
  }

  public async pollingAction(): Promise<void> {}

  public async stopPollingAction(): Promise<void> {}

  public handlePollingSuccess(): void {
    if (!this.authService.authenticated$.value) {
      this.stopPolling();
      return;
    }
    this.pollFailures = 0;
    this.intervalTimeoutSec = this.defaultTimeoutSec;
    this.resetPollingInterval();
  }

  public handlePollingError(): void {
    if (!this.authService.authenticated$.value) {
      this.stopPolling();
      return;
    }

    if (this.pollFailures === 0) {
      this.intervalTimeoutSec = this.startingFailureIntervalSec;
    }

    this.pollFailures++;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (this.pollFailures % 2 === 0) {
      if (this.intervalTimeoutSec < this.maxRetryTimeoutSec) {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        const increasedTimeout = this.intervalTimeoutSec * 2;
        this.intervalTimeoutSec = increasedTimeout < this.maxRetryTimeoutSec ? increasedTimeout : this.maxRetryTimeoutSec;
      }
    }

    this.resetPollingInterval();
  }

  private resetPollingInterval(): void {
    this.intervalTimeoutSec = this.intervalTimeoutSec || this.defaultTimeoutSec;

    if (this.interval) {
      clearInterval(this.interval);
    }
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.interval = setInterval(this.pollingAction.bind(this), this.intervalTimeoutSec * 1000);
  }
}
